import React, { useState } from 'react';
import { Star, ThumbsUp, User, Flag, MoreHorizontal } from 'lucide-react';
import type { Review } from '../../types';
import { formatRelativeTime } from '../../utils/formatters';

interface ReviewCardProps {
  review: Review;
  userId?: string;
  onMarkHelpful: (reviewId: string) => void;
  onFlag?: (reviewId: string) => void;
}

export function ReviewCard({ review, userId, onMarkHelpful, onFlag }: ReviewCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const hasVoted = userId ? review.helpfulVoters.includes(userId) : false;
  const isOwnReview = userId === review.userId;
  const hasFlagged = userId && review.flaggedBy?.includes(userId);

  const handleFlag = () => {
    if (onFlag && !isOwnReview) {
      onFlag(review.id);
      setShowMenu(false);
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 relative">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {review.userProfilePicture ? (
            <img
              src={review.userProfilePicture}
              alt={review.userName}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-indigo-500" />
            </div>
          )}
          <div>
            <div className="font-medium text-gray-800">{review.userName}</div>
            <div className="text-sm text-gray-500">
              {formatRelativeTime(review.createdAt)}
            </div>
          </div>
        </div>

        {/* Stars and Menu */}
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-4 h-4 ${
                  star <= review.rating
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Menu Button for Flag */}
          {userId && !isOwnReview && onFlag && (
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 hover:bg-gray-200 rounded transition"
              >
                <MoreHorizontal className="w-4 h-4 text-gray-400" />
              </button>

              {showMenu && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />
                  {/* Menu */}
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[140px]">
                    <button
                      onClick={handleFlag}
                      disabled={!!hasFlagged}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition ${
                        hasFlagged
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-red-600 hover:bg-red-50'
                      }`}
                    >
                      <Flag className="w-4 h-4" />
                      {hasFlagged ? 'Gemeldet' : 'Melden'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Text */}
      <p className="text-gray-700 mb-3">{review.text}</p>

      {/* Visit Date */}
      {review.visitDate && (
        <p className="text-sm text-gray-500 mb-3">Besuch: {review.visitDate}</p>
      )}

      {/* Helpful Button */}
      {!isOwnReview && userId && (
        <button
          onClick={() => onMarkHelpful(review.id)}
          className={`flex items-center gap-1 text-sm transition-colors ${
            hasVoted
              ? 'text-indigo-600'
              : 'text-gray-500 hover:text-indigo-600'
          }`}
        >
          <ThumbsUp className={`w-4 h-4 ${hasVoted ? 'fill-current' : ''}`} />
          <span>Hilfreich ({review.helpful})</span>
        </button>
      )}

      {/* Show helpful count for own reviews or logged out users */}
      {(isOwnReview || !userId) && review.helpful > 0 && (
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <ThumbsUp className="w-4 h-4" />
          <span>{review.helpful} fanden dies hilfreich</span>
        </div>
      )}
    </div>
  );
}
